import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Button from '../../../components/button/button.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import IconTextBlock from '../../../components/icon-text-block/icon-text-block.component';
import DownloadIcon from '../../../icons/download-icon';
import { DEAL_SOURCES_LIST_VIEW, DEAL_USES_LIST_VIEW } from '../../../constants/entity/permissions';

const DealCapitalizationFile: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isNewDeal,
    isDealCapitalizationFileDeleted,
    dealCapitalizationFileData,
    dealCapitalizationFileFile,
    onFileChange,
    onFileDelete,
    onFileViewClick,
  } = props;

  const [isExpanded, setIsExpanded]: [boolean, Function] = useState(true);

  const dealCapitalizationListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_SOURCES_LIST_VIEW,
    DEAL_USES_LIST_VIEW,
  ], []);
  const formPrefix: string = 'dealData.dealCapitalizationFileData';

  const fileDownloadClickDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealCapitalizationFileData,
  ], [dealCapitalizationFileData]);

  const onExpandPanel: Function = useCallback((): void => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const dealFileFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file);
    }
  }, [dealCapitalizationFileFile]);

  const dealFileFileDelete: Function = useCallback((isNew?: boolean): void => {
    if (onFileDelete) {
      onFileDelete(isNew);
    }
  }, [dealCapitalizationFileFile]);

  return (
    <CheckAccess permissions={dealCapitalizationListViewPermissions}>
      {
        ((dealCapitalizationFileData && dealCapitalizationFileData.file) || isEditMode || isNewDeal) && (
          <ExpansionGroup
            withOffset
            isExpanded={isExpanded}
            headerComponent="Capitalization File"
            onChange={onExpandPanel}
          >
            <Card isContainer>
              {
                !isEditMode && !isNewDeal && (
                  <FlexContainer>
                    {
                      dealCapitalizationFileData.file && dealCapitalizationFileData.file.mimeType && (
                        <FilteredLink
                          isExternal
                          link={dealCapitalizationFileData.file.link}
                          clickDeps={fileDownloadClickDeps}
                          onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onFileViewClick(event, dealCapitalizationFileData.file);
                          }}
                        >
                          <IconTextBlock icon={<DownloadIcon />}>
                            Download
                          </IconTextBlock>
                        </FilteredLink>
                      )
                    }
                    {
                      dealCapitalizationFileData.user && (
                        <PaddedFlexContainer>
                          Uploaded By
                          {' '}
                          {dealCapitalizationFileData.user.fullName}
                        </PaddedFlexContainer>
                      )
                    }
                  </FlexContainer>
                )
              }
              {
                (isEditMode || isNewDeal) && (
                  <FlexContainer>
                    <FormFieldUpload
                      isDroppable
                      uploadFile={dealCapitalizationFileFile}
                      name={`${formPrefix}.file`}
                      buttonLabel="Upload New File"
                      onFileChange={dealFileFileChange}
                      onFileDelete={(): void => {
                        dealFileFileDelete(true);
                      }}
                    />
                    {
                      (!isDealCapitalizationFileDeleted && !dealCapitalizationFileFile
                        && dealCapitalizationFileData && dealCapitalizationFileData.file) && (
                        <FlexContainer isInline>
                          <Button
                            isText
                            isActive
                            onClick={(): void => {
                              dealFileFileDelete();
                            }}
                          >
                            Delete
                          </Button>
                        </FlexContainer>
                      )
                    }
                  </FlexContainer>
                )
              }
            </Card>
          </ExpansionGroup>
        )
      }
    </CheckAccess>
  );
});

export default DealCapitalizationFile;
